import { Datepicker as ACPaaSDatepicker, Icon } from '@acpaas-ui/react-components';
import 'moment/locale/nl';
import { useEffect, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { INPUT_STATES } from './forms.utils';

const Datepicker = ({
  value = '',
  state = INPUT_STATES.READ,
  onChange,
  onCancel,
  onSubmit,
  onEditStart,
  hasError,
  description,
  validationError,
  required,
  readOnly,
  ...props
}) => {
  const id = useRef(uuid()).current;
  const [isOpen, setIsOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isSubmittingAfterCalendarClick, setIsSubmittingAfterCalendarClick] = useState(false);
  const isSubmitting = isSubmittingAfterCalendarClick || state === INPUT_STATES.SUBMITTING;
  const isEdit = state === INPUT_STATES.EDIT;
  const isRead = state === INPUT_STATES.READ;

  const close = () => {
    setIsOpen(false);
    document.getElementById(id)?.blur();
  };

  const handleKeyDown = (event) => {
    if (isCalendarOpen) {
      return;
    }

    if (event?.code === 'Enter') {
      onSubmit?.(null, close);
    }
    if (event?.code === 'Escape') {
      onCancel();
      close();
    }
  };

  const handleChange = (value) => {
    if (isCalendarOpen) {
      setIsSubmittingAfterCalendarClick(true);
      onSubmit?.(value, () => {
        setIsSubmittingAfterCalendarClick(false);
        close();
      });
      setIsCalendarOpen(false);
      return;
    }
    onChange(value);
  };

  const handleClick = (e) => {
    if (isSubmitting || props.disabled || readOnly) {
      return;
    }
    const calendarOpenButton = document.querySelector(`#js-spo-datepicker-${id} span.ai.is-clickable`);
    if (e.target === calendarOpenButton || calendarOpenButton.contains(e.target)) {
      setIsCalendarOpen(!isCalendarOpen);
    }
    onEditStart();
    setIsOpen(true);
  };

  const handleFocus = () => {
    if (isSubmitting || props.disabled || readOnly) {
      return;
    }
    onEditStart();
    setIsOpen(true);
  };

  const handleClear = (event) => {
    event.stopPropagation();
    onSubmit?.('', close);
  };

  const blockActionKeydownPropagation = (event) => {
    if (event.code !== 'Escape') {
      event.stopPropagation();
    }
  };

  useEffect(() => {
    const keydownCalendarButton = (event) => {
      if (event.code === 'Enter') {
        setIsCalendarOpen(!isCalendarOpen);
        event.stopPropagation();
      }
    };

    const calendarOpenButton = document.querySelector(`#js-spo-datepicker-${id} span.ai.is-clickable`);
    calendarOpenButton.addEventListener('keydown', keydownCalendarButton);

    return () => {
      calendarOpenButton.removeEventListener('keydown', keydownCalendarButton);
    };
  }, [id, isCalendarOpen]);

  return (
    <div
      id={`js-spo-datepicker-${id}`}
      className={`spo-a-datepicker ${isOpen ? '' : isRead || readOnly ? 'readonly' : ''} ${
        isSubmitting ? 'submitting' : ''
      } ${isEdit ? 'edit' : ''}${readOnly ? 'read-only' : ''}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onFocus={handleFocus}
      onBlur={() => {
        // TODO when it has blur, also blurs and triggers blur on clicking the actions
        // can be figured out with use of useOutsideClick hook
        // close();
        // onCancel();
      }}
    >
      <ACPaaSDatepicker
        {...props}
        id={id}
        read
        format={'DD/MM/YYYY'}
        activeDate={value}
        onChange={handleChange}
        disabled={isSubmitting || props.disabled || readOnly}
        state={hasError || !!validationError ? 'error' : ''}
        required={required}
        description={
          validationError || !!description ? (
            <span className="u-text-danger">{validationError || description}</span>
          ) : (
            ''
          )
        }
        mask={'99/99/9999'}
      ></ACPaaSDatepicker>
      {!isOpen && !value && <span className="spo-a-datepicker__empty-placeholder">Geen</span>}
      {isSubmitting && <span className="a-spinner a-spinner--sm spinner ai"></span>}
      <div className={`actions ${!isOpen && !isEdit ? 'hidden' : ''}`}>
        {/* ACPaaS Button doest support setting tab index */}
        <button
          className="a-button a-button--success a-button--small has-icon"
          aria-label="Submit"
          disabled={isSubmitting || props.disabled || readOnly}
          onKeyDown={blockActionKeydownPropagation}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onSubmit?.(null, close);
          }}
          tabIndex={!isOpen ? '-1' : ''}
        >
          <Icon name="ai-check-1" />
        </button>
        <button
          className="a-button a-button--danger a-button--small a-button-outline has-icon"
          aria-label="Cancel"
          disabled={isSubmitting || props.disabled || readOnly}
          onKeyDown={blockActionKeydownPropagation}
          onClick={(e) => {
            e.stopPropagation();
            onCancel();
            close();
          }}
          tabIndex={!isOpen ? '-1' : ''}
        >
          <Icon name="ai-close" />
        </button>
        {!!value && value !== '__/__/____' && !required && (
          <button
            className="a-button a-button--danger a-button--small a-button-outline clear"
            aria-label="Geen"
            disabled={isSubmitting || props.disabled}
            onKeyDown={blockActionKeydownPropagation}
            onClick={handleClear}
            tabIndex={!isOpen ? '-1' : ''}
          >
            Wissen
          </button>
        )}
      </div>
    </div>
  );
};

export default Datepicker;
